import { useRef, useState } from "react";
import { TIER_ORDER, TIER_COLORS } from "../utils/tiers";
import PlayerCard from "../components/PlayerCard";
import AddPlayerModal from "../components/AddPlayerModal";
import { hasUnmetPartnerPreference } from "../utils/validation";
import { parseUploadedPlayers, normalizeUploadedPlayer } from "../utils/importPlayers";
import { parseMemberPreferencesCSV } from "../utils/importMemberPreferences";
import { isHouseholdCSV, parseHouseholdCSV } from "../utils/households";
import PlayerStatsPanel from "../components/PlayerStatsPanel";
import { isDeferred, hasNoFit, needsPlacing } from "../utils/placement";
import { FOLLOW_UPS, countFollowUp, matchesAnyFollowUp } from "../utils/followUps";

export default function RosterView({ players, onPlayerClick, onUploadPlayers, onLinkHouseholds, onNotify }) {
  const [search, setSearch] = useState("");
  const [filterTier, setFilterTier] = useState("All");
  const [uploadError, setUploadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("players"); // "players" | "insights"
  const [onlyMissingEmail, setOnlyMissingEmail] = useState(false);
  // Active follow-up tag keys. Several selected widens the list to anyone
  // carrying at least one of them — see matchesAnyFollowUp.
  const [followUpFilter, setFollowUpFilter] = useState([]);
  const fileInputRef = useRef(null);

  function toggleFollowUpFilter(key) {
    setFollowUpFilter(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }

  function handleAddPlayer(draft) {
    const player = normalizeUploadedPlayer(draft, new Set(players.map(p => p.id)));
    if (!player) {
      onNotify({ message: "Couldn't add player — a name is required." });
      return;
    }
    onUploadPlayers([player]);
    setAdding(false);
    onNotify({ message: `Added ${player.name}` });
  }

  function handleUploadFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      // Member Preferences form exports are CSV; the roster/kac_data files are
      // JSON. Route by extension (falling back to a CSV sniff for a bare .csv
      // that browsers sometimes report without an extension).
      const isCSV = /\.csv$/i.test(file.name) ||
        (!/\.json$/i.test(file.name) && /,/.test(text) && !/^\s*[[{]/.test(text));

      // The club's member import file adds no players — it groups the ones
      // already on the roster into households, so either parent's email pulls
      // up the whole family in "My Schedule".
      if (isCSV && isHouseholdCSV(text)) {
        const parsed = parseHouseholdCSV(text);
        if (parsed.error) {
          setUploadError(parsed.error);
          return;
        }
        setUploadError(null);
        const report = onLinkHouseholds(parsed.households);
        const missed = report.unmatched.length;
        onNotify({
          message: missed > 0
            ? `Linked ${report.linked} player${report.linked !== 1 ? "s" : ""} into ${report.households} household${report.households !== 1 ? "s" : ""} — ${missed} family member${missed !== 1 ? "s are" : " is"} not on the roster yet`
            : `Linked ${report.linked} player${report.linked !== 1 ? "s" : ""} into ${report.households} household${report.households !== 1 ? "s" : ""}`,
        });
        return;
      }

      const result = isCSV
        ? parseMemberPreferencesCSV(text, players.map(p => p.id))
        : parseUploadedPlayers(text, players.map(p => p.id));
      if (result.error) {
        setUploadError(result.error);
        return;
      }
      setUploadError(null);
      if (result.players.length > 0) onUploadPlayers(result.players);
      onNotify({
        message: result.skipped > 0
          ? `Added ${result.players.length} player${result.players.length !== 1 ? "s" : ""}, skipped ${result.skipped} invalid entr${result.skipped !== 1 ? "ies" : "y"}`
          : `Added ${result.players.length} player${result.players.length !== 1 ? "s" : ""}`,
      });
    };
    reader.readAsText(file);
  }

  const tierCounts = {};
  TIER_ORDER.forEach(t => { tierCounts[t] = 0; });
  players.forEach(p => { if (tierCounts[p.tier] !== undefined) tierCounts[p.tier]++; });

  // Members with no address of their own can still be reached if a parent or
  // spouse shares their household id — those are the ones actually stranded.
  const emailedHouseholds = new Set(
    players.filter(p => String(p.email || "").trim() && String(p.householdId ?? "").trim())
      .map(p => String(p.householdId).trim())
  );
  const isUnreachable = p =>
    !String(p.email || "").trim() &&
    !emailedHouseholds.has(String(p.householdId ?? "").trim());
  const noEmailCount = players.filter(p => !String(p.email || "").trim()).length;

  const filtered = players.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    const matchTier = filterTier === "All" || p.tier === filterTier;
    const matchEmail = !onlyMissingEmail || !String(p.email || "").trim();
    const matchFollowUp = matchesAnyFollowUp(p, followUpFilter);
    return matchSearch && matchTier && matchEmail && matchFollowUp;
  });

  return (
    <div className="p-4">
      {adding && (
        <AddPlayerModal players={players} onSubmit={handleAddPlayer} onClose={() => setAdding(false)} />
      )}

      <div className="inline-flex mb-4 rounded-lg border border-gray-300 overflow-hidden text-sm">
        <button
          onClick={() => setTab("players")}
          className={`px-4 py-1.5 transition-colors ${tab === "players" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        >
          Players
        </button>
        <button
          onClick={() => setTab("insights")}
          className={`px-4 py-1.5 border-l border-gray-300 transition-colors ${tab === "insights" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        >
          Insights
        </button>
      </div>

      {tab === "insights" ? (
        <PlayerStatsPanel players={players} />
      ) : (
      <>
      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search by name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <select
          value={filterTier}
          onChange={e => setFilterTier(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="All">All tiers</option>
          {TIER_ORDER.map(t => (
            <option key={t} value={t}>{t} ({tierCounts[t]})</option>
          ))}
        </select>
        {noEmailCount > 0 && (
          <button
            onClick={() => setOnlyMissingEmail(v => !v)}
            title="Show only members with no email address — click one to add it"
            className={`text-sm px-3 py-1.5 rounded border transition-colors ${
              onlyMissingEmail
                ? "bg-amber-500 border-amber-500 text-white"
                : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
            }`}
          >
            ✉ No email ({noEmailCount})
          </button>
        )}
        {FOLLOW_UPS.map(tag => {
          const count = countFollowUp(players, tag.key);
          if (count === 0) return null;
          const on = followUpFilter.includes(tag.key);
          return (
            <button
              key={tag.key}
              onClick={() => toggleFollowUpFilter(tag.key)}
              title={`Show only members tagged "${tag.label}" — tag them from their card`}
              className={`text-sm px-3 py-1.5 rounded border transition-colors ${
                on ? tag.active : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
              }`}
            >
              {tag.label} ({count})
            </button>
          );
        })}
        <button
          onClick={() => setAdding(true)}
          className="sm:ml-auto flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded transition-colors"
        >
          + Add player
        </button>
        <button
          onClick={() => fileInputRef.current.click()}
          className="flex items-center gap-1.5 border border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 text-sm px-3 py-1.5 rounded transition-colors"
          title="Roster JSON or a Member Preferences CSV adds players. The club's member import CSV (ID / Child / First Name…) instead links existing players into households for the My Schedule lookup."
        >
          ↑ Upload players
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,.csv,application/json,text/csv"
          onChange={e => { handleUploadFile(e.target.files[0]); e.target.value = ""; }}
          className="hidden"
        />
      </div>

      {uploadError && (
        <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{uploadError}</p>
      )}

      {onlyMissingEmail && (() => {
        const stranded = filtered.filter(isUnreachable);
        return (
          <p className="mb-4 text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            {filtered.length} shown with no email of their own.{" "}
            {stranded.length === 0
              ? "All of them are reachable through someone in their household."
              : `${stranded.length} can't be reached at all — no address and no household member with one: ${stranded.map(p => p.name).join(", ")}.`}
            {" "}Click a member to add an address or a household id.
          </p>
        );
      })()}

      {followUpFilter.length > 0 && (
        <p className="mb-4 text-sm text-gray-600 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2">
          {filtered.length} member{filtered.length !== 1 ? "s" : ""} tagged{" "}
          {followUpFilter
            .map(k => `"${FOLLOW_UPS.find(f => f.key === k).label}"`)
            .join(" or ")}
          . Open a member to see their availability and assign them, then untick the tag on their card.
        </p>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="text-amber-500">⚠</span> Not available at assigned time</span>
        <span className="flex items-center gap-1"><span className="text-purple-500">👥</span> Preferred partner not in same group</span>
      </div>

      <div className="space-y-6">
        {(() => {
          const unassigned = filtered.filter(needsPlacing);
          if (unassigned.length === 0) return null;

          const sorted = [...unassigned].sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
          const colors = TIER_COLORS["No Group"];
          return (
            <div>
              <div className={`${colors.header} ${colors.text} px-3 py-1.5 rounded-t font-semibold text-sm flex justify-between`}>
                <span>Unassigned</span>
                <span>{unassigned.length} players</span>
              </div>
              <div className={`${colors.bg} ${colors.border} border border-t-0 rounded-b p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2`}>
                {sorted.map(p => (
                  <PlayerCard
                    key={p.id}
                    player={p}
                    partnerFlag={hasUnmetPartnerPreference(p, players)}
                    onClick={() => onPlayerClick(p)}
                  />
                ))}
              </div>
            </div>
          );
        })()}
        {/* Members sitting this term out. Kept out of Unassigned so that
            pile stays a list of work, and shown after it so nobody has to
            scroll past people who need nothing. */}
        {(() => {
          const sittingOut = filtered.filter(isDeferred);
          if (sittingOut.length === 0) return null;

          const sorted = [...sittingOut].sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
          return (
            <div>
              <div className="bg-slate-200 text-slate-700 px-3 py-1.5 rounded-t font-semibold text-sm flex justify-between">
                <span>Waiting for next term — no group needed</span>
                <span>{sittingOut.length} {sittingOut.length === 1 ? "person" : "people"}</span>
              </div>
              <div className="bg-slate-50 border border-t-0 border-dashed border-slate-300 rounded-b p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {sorted.map(p => (
                  <PlayerCard
                    key={p.id}
                    player={p}
                    onClick={() => onPlayerClick(p)}
                  />
                ))}
              </div>
            </div>
          );
        })()}
        {/* Members who want a group but can't make any clinic the club runs.
            Also kept out of Unassigned — a coach can't place someone into a
            slot that doesn't exist — but listed separately from the people
            sitting out, because this pile is the argument for adding a time. */}
        {(() => {
          const noFit = filtered.filter(hasNoFit);
          if (noFit.length === 0) return null;

          const sorted = [...noFit].sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
          return (
            <div>
              <div className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-t font-semibold text-sm flex justify-between">
                <span>Can't make any clinic time — no slot on the schedule fits</span>
                <span>{noFit.length} {noFit.length === 1 ? "person" : "people"}</span>
              </div>
              <div className="bg-amber-50 border border-t-0 border-dashed border-amber-300 rounded-b p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {sorted.map(p => (
                  <PlayerCard
                    key={p.id}
                    player={p}
                    onClick={() => onPlayerClick(p)}
                  />
                ))}
              </div>
            </div>
          );
        })()}
        {TIER_ORDER.map(tier => {
          const tierPlayers = filtered.filter(p => p.tier === tier);
          if (tierPlayers.length === 0) return null;

          const sorted = [...tierPlayers].sort((a, b) => {
            if (tier.startsWith("Junior")) {
              if (a.age === null && b.age === null) return 0;
              if (a.age === null) return 1;
              if (b.age === null) return -1;
              return a.age - b.age;
            }
            return (a.lastName || "").localeCompare(b.lastName || "");
          });

          const colors = TIER_COLORS[tier];
          return (
            <div key={tier}>
              <div className={`${colors.header} ${colors.text} px-3 py-1.5 rounded-t font-semibold text-sm flex justify-between`}>
                <span>{tier}</span>
                <span>{tierPlayers.length} players</span>
              </div>
              <div className={`${colors.bg} ${colors.border} border border-t-0 rounded-b p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2`}>
                {sorted.map(p => (
                  <PlayerCard
                    key={p.id}
                    player={p}
                    showGroup
                    partnerFlag={hasUnmetPartnerPreference(p, players)}
                    onClick={() => onPlayerClick(p)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}
